import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Truck, Plus, Pencil, Trash, Phone, Envelope, MapPin, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", contact_name: "", phone: "", email: "", address: "", notes: "", categories: "" });

  useEffect(() => { fetchSuppliers(); }, []);

  const fetchSuppliers = async () => {
    try { setSuppliers((await axios.get(`${API}/suppliers`)).data); }
    catch { toast.error("Erro ao carregar fornecedores"); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }
    const payload = { ...form, categories: form.categories ? form.categories.split(",").map(c => c.trim()).filter(Boolean) : [] };
    try {
      if (editing) { await axios.put(`${API}/suppliers/${editing.id}`, payload); toast.success("Fornecedor atualizado!"); }
      else { await axios.post(`${API}/suppliers`, payload); toast.success("Fornecedor criado!"); }
      setDialogOpen(false); resetForm(); fetchSuppliers();
    } catch { toast.error("Erro ao salvar"); }
  };

  const handleEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, contact_name: s.contact_name || "", phone: s.phone || "", email: s.email || "", address: s.address || "", notes: s.notes || "", categories: (s.categories || []).join(", ") });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deletar este fornecedor?")) return;
    try { await axios.delete(`${API}/suppliers/${id}`); toast.success("Deletado!"); fetchSuppliers(); }
    catch { toast.error("Erro ao deletar"); }
  };

  const resetForm = () => { setForm({ name: "", contact_name: "", phone: "", email: "", address: "", notes: "", categories: "" }); setEditing(null); };

  const filtered = suppliers.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" /></div>;

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold tracking-tight text-zinc-950">{t("sup_title")}</h2>
          <p className="text-xs md:text-sm text-zinc-500 mt-1">{t("sup_subtitle")}</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-zinc-950 text-white hover:bg-zinc-800" data-testid="add-supplier-btn">
          <Plus size={16} className="mr-2" /> {t('sup_new')}
        </Button>
      </div>

      {suppliers.length > 0 && (
        <div className="relative max-w-xs">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("sup_search")} className="pl-9 h-9 text-sm" />
        </div>
      )}

      {filtered.length === 0 ? (
        <Card><CardContent className="text-center py-12">
          <Truck size={48} className="mx-auto text-zinc-300 mb-3" />
          <p className="text-zinc-500">{t("sup_no_suppliers")}</p>
        </CardContent></Card>
      ) : (
        <div className="border border-zinc-200 rounded-sm overflow-hidden">
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow className="bg-zinc-50">
              <TableHead className="text-xs font-semibold">Nome</TableHead>
              <TableHead className="text-xs font-semibold">Contato</TableHead>
              <TableHead className="text-xs font-semibold">Telefone</TableHead>
              <TableHead className="text-xs font-semibold">Categorias</TableHead>
              <TableHead className="text-xs font-semibold text-center">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((s) => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-medium text-zinc-900">{s.name}</TableCell>
                  <TableCell className="text-sm text-zinc-600">{s.contact_name || "—"}</TableCell>
                  <TableCell className="text-sm text-zinc-600 font-mono">{s.phone || "—"}</TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{(s.categories || []).map(c => (
                    <span key={c} className="text-[10px] bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{c}</span>
                  ))}</div></TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(s)}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500" onClick={() => handleDelete(s.id)}><Trash size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editing ? t('sup_edit') : t('sup_new')}</DialogTitle>
            <DialogDescription className="sr-only">Formulário de fornecedor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} placeholder="Ex: Mercado Central" className="mt-1.5" data-testid="supplier-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Contato</Label>
                <Input value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} placeholder="Nome do contato" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Telefone</Label>
                <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="(11) 99999-9999" className="mt-1.5" data-testid="supplier-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Email</Label>
              <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} placeholder="fornecedor@email.com" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Endereço</Label>
              <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="Rua, número, bairro" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Categorias (separadas por vírgula)</Label>
              <Input value={form.categories} onChange={(e) => setForm({...form, categories: e.target.value})} placeholder="Hortifruti, Secos, Carnes" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Observações</Label>
              <textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Notas..." rows={2} className="w-full mt-1.5 px-3 py-2 border border-zinc-200 rounded-md text-sm" />
            </div>
            <Button onClick={handleSave} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="save-supplier-btn">
              {editing ? "Atualizar" : "Criar Fornecedor"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
